import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getFolderMatrix } from "../../services/questionService";
import { isCanceledError } from "../../utils/examError";
import VariantList from "./VariantList";
import VariantDetail from "./VariantDetail";

// The subject's matrix by grade (GET question_folders/<uuid>/matrix/ → grades): the grades a variant can be built
// for (1–12) with what they plan, and their quarters' roles.
function useMatrixGrades(folderUuid) {
  const [state, setState] = useState({ loading: true, error: null, grades: [] });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!folderUuid) return undefined;
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));
    getFolderMatrix(folderUuid, { signal: controller.signal })
      .then((matrix) => {
        const grades = (matrix?.grades || []).filter((g) => Number(g.grade) >= 1 && Number(g.grade) <= 12);
        setState({ loading: false, error: null, grades });
      })
      .catch((err) => {
        if (!isCanceledError(err)) setState({ loading: false, error: err, grades: [] });
      });
    return () => controller.abort();
  }, [folderUuid, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

/**
 * The Subject variant tab of a subject in the bank (its moderator and admins): the subject's variants (the BTS
 * ones) by grade, built from its matrix. The open variant is in the address (?variant=<uuid>), so a reload or a
 * link from the BTS pages opens it; going back to the list drops it and keeps the other parameters.
 * `folder` is the subject (an item of GET question_folders/).
 */
export default function SubjectVariants({ folder }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const variantUuid = searchParams.get("variant");
  const matrix = useMatrixGrades(folder?.uuid);

  // Opening a variant is a step the browser's Back undoes; `replace` for a variant that is not of this subject.
  const openVariant = useCallback(
    (uuid, { replace = false } = {}) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (uuid) params.set("variant", uuid);
          else params.delete("variant");
          return params;
        },
        { replace }
      ),
    [setSearchParams]
  );

  // Once the view changed here (a variant opened, created or deleted, the way back taken), the new one takes the
  // focus (its title); the first one, from a link or a reload, leaves it where the page puts it.
  const firstView = useRef(variantUuid);
  const moved = useRef(false);
  if (variantUuid !== firstView.current) moved.current = true;

  if (!folder) return null;
  return variantUuid ? (
    <VariantDetail
      key={variantUuid}
      uuid={variantUuid}
      folder={folder}
      matrix={matrix}
      autoFocus={moved.current}
      onBack={(options) => openVariant(null, options)}
    />
  ) : (
    <VariantList folder={folder} matrix={matrix} autoFocus={moved.current} onOpen={openVariant} />
  );
}
